import {_, React, observer, computed, IDisposer, action, useEffect, classNames, useAsObservableSource, pluralize, Fragment} from '@api'
import {Checkbox, Chip} from '@material-ui/core'
import {ChangeLogEntriesForCatalogItemAtTimestamp} from './entries'
import {ChangeLogFlexLayoutTabStore, ChangeLogItemsStore, ChangeLogSingleItemMoreOptions, useChangeLogFlexLayoutTabStore, useChangeLogItemsStore, useVipLocalStore, utils, VipLocalStore} from '@modules'
import {StoresInstance, useCatalog, Workspace} from '@stores'
import {MyImage} from 'centric-ui-components'
import {runInAction, toJS, when} from 'mobx'
import moment from 'moment'
import {useContainerQuery} from 'react-container-query'
import * as css from './ChangeLogSingleItem.css'

/// region Store

export enum ChangeLogOperationTypes {
  Added = 'Added',
  Changed = 'Changed',
  Removed = 'Deleted',
  Created = 'Added',
  'Inner Change' = 'Changed',
  Deleted = 'Deleted'
}


export class ChangeLogSingleItemStore extends VipLocalStore {
  constructor(stores: StoresInstance, public itemsStore: ChangeLogItemsStore, public root: ChangeLogFlexLayoutTabStore, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  get id() {
    return this.changed.id
  }

  get item() {
    const {itemsStore} = this
    return itemsStore.item
  }

  get changed() {
    const {props} = this
    return props.changed
  }

  get log() {
    const {itemsStore} = this
    return itemsStore.log
  }

  @computed get operation() {
    const {changed} = this
    return ChangeLogOperationTypes[changed.op]
  }

  @computed get workspace() : Workspace {
    const {itemsStore: {workspace}} = this
    return workspace
  }

  @computed get dataMap() {
    const {workspace} = this
    if (!workspace) {
      return null
    }
    return workspace.dataMap
  }

  @computed get field() {
    if (!this.workspace) {
      return null
    }
    const {workspace: {catalog: {_fieldByName}}, changed} = this
    return _fieldByName[changed.field]
  }

  @computed get isImageField() {
    const {field} = this
    return field?.field === 'images'
  }

  get nested() {
    return this.props.nested
  }

  get label() {
    return this.props.label
  }

  @computed get fieldLabel() {
    if (!this.workspace) {
      return null
    }
    const {field, nested, label, workspace: {catalog}} = this
    if (field) {
      const fieldLabel = catalog.labelForFieldName_currentCatalog(field.field)
      return nested ? `${label} ${nested}'s ${fieldLabel}` : fieldLabel
    }
    return null
  }

  @computed get after() {
    const {item, field, es, changed: {diff}, strings} = this
    let value = field?.esIndex ? es.readField(item, field?.field, field.group?.labelField ?? field.group?.field).valAsString : _.isString(diff.after) ? diff.after : JSON.stringify(diff.after)
    if (field?.def?.type === 'Boolean') {
      value = _.capitalize(value === 'true' ? (field.def?.booleanLabels?.trueLabel ?? strings.yes) : (field.def?.booleanLabels?.falseLabel ?? strings.no))
    }
    return value
  }

  @computed get before() {
    const {item, field, es, delta, operation, changed: {diff}, strings} = this
    if (!field) return {}
    let value = operation === 'Deleted' && delta !== null ? _.get(_.first(delta[field.field]), 'name') : field?.esIndex ? es.readField(item, field?.field, field.group?.labelField ?? field.group?.field).valAsString : _.isString(diff.before) ? diff.before : JSON.stringify(diff.before)
    if (field?.def?.type === 'Boolean') {
      value = _.capitalize(value === 'true' ? (field.def?.booleanLabels?.trueLabel ?? strings.yes) : (field.def?.booleanLabels?.falseLabel ?? strings.no))
    }
    return value
  }

  @computed get itemName() {
    const {item, dataMap: {title}, root: {config}, stores} = this
    return _.isFunction(config?.title) ? config.title(item, title, stores) : _.get(item, title)
  }

  @action onClickItemName = () => {
    const {root: {onClickItemOpenInDetailsPane_old}, item, workspace} = this
    return onClickItemOpenInDetailsPane_old(item, workspace)
  }

  @action onClickOpenInSidebar = () => {
    const {item, workspace, stores: {sidebar: {right}}, catalog} = this,
      path = catalog.pathForData(null, item, workspace?.config)

    right.updateState({open: true})
    right.path = path.slice()
    catalog.focusedCatalogItemId = catalog.idFor(item)
  }

  @action onClickOpenWorkspace = (e) => {
    const {item, workspace, stores: {flexLayout, flexLayout: {create}}} = this,
      tab = create.newCatalogTab({workspace: workspace.config, selectNewTab: true})
  }

  @action onClickMoveToSeason = (e?: React.MouseEvent) => {
    const {workspace, stores: {flexLayout: {create}}} = this,
      tab = create.newCatalogTab({
        workspace: workspace.config, selectNewTab: true
      })

    setTimeout(() => {
      const {catalogRoot: {catalog}} = tab, {item} = this
      when(() => catalog.hasLoadedConfig, () => {
        catalog.navigateToHierarchy(item)
      })
    }, 2000)
  }

  @computed get imageCountLabel() {
    const {operation, field, before, after, isImageField} = this
    if (!isImageField) {
      return null
    }
    const afterLength = utils.isJson(after) ? JSON.parse(after).length : 0,
      beforeLength = utils.isJson(before) ? JSON.parse(before).length : 0

    switch (operation) {
      case 'Added':
        return `${afterLength} ${pluralize('image', afterLength)}`
      case 'Deleted':
        return `${beforeLength} ${pluralize('image', beforeLength)}`
    }
  }

  @computed get myImages() {
    const {operation, field, before, after, isImageField, catalog} = this
    if (!isImageField) {
      return null
    }
    // console.log({operation, before, after})
    switch (operation) {
      case 'Added':
        return <>{!utils.isJson(after) ? '' : _.map(JSON.parse(after), id => (<MyImage
          width={40}
          height={40}
          key={id}
          className={classNames(css.changedImage)}
          imageUrl={catalog.imageUrlFor({id})}
        />))}</>
      case 'Deleted':
        return <>{!utils.isJson(before) ? '' : _.map(JSON.parse(before), id => (<MyImage
          width={40}
          height={40}
          key={id}
          className={classNames(css.changedImage)}
          imageUrl={catalog.imageUrlFor({id})}
        />))}</>
    }
  }

  @computed get __html() {
    const {operation, fieldLabel, itemName, before, after, onClickItemName, imageCountLabel, myImages, root: {settings: {newLook}}} = this
    switch (operation) {
      case 'Added': {
        return (
          <>
            <span className={classNames(css.changeText, imageCountLabel && css.changeTextWithImage)}>
              {imageCountLabel ?? <>
                  <Fragment><span data-type={'field-label'}>{fieldLabel}</span>&nbsp;</Fragment>
	              <span>&nbsp;{after}</span> to&nbsp;
	              <span data-type={'item-name-url'} onClick={onClickItemName}>{itemName}</span></>}
                </span>
            {myImages}
          </>
        )
      }
      case 'Changed': {
        return (<span className={css.changeText}>
          <Fragment><span data-type={'field-label'}>{fieldLabel}</span>&nbsp;</Fragment>
            <span data-type='before-text'>{before}</span>
            &nbsp;to {after} in&nbsp;
            <span data-type={'item-name-url'} onClick={onClickItemName}>{itemName}</span>
        </span>
        )
      }
      case 'Deleted': {
        return (
          <>
            <span className={classNames(css.changeText, imageCountLabel && css.changeTextWithImage)}>
              {imageCountLabel ?? <><span data-type={'field-label'}>{fieldLabel}</span>&nbsp;<span data-type='before-text'>{_.isObject(before) ? '' : before}</span></>}
              &nbsp;from&nbsp;
              <span data-type={'item-name-url'} onClick={onClickItemName}>{itemName}</span>
            </span>
            {myImages}
          </>
        )
      }
    }
    return null
  }

  @computed get delta() {
    return JSON.parse(this.log.delta)
  }

  @computed get checked() {
    const {root: {_selected}, id} = this
    return _selected.includes(id)
  }

  @computed get userInfo() {
    const {stores, log} = this, {users: {getById}} = stores,
      userInfo = getById(log.userId)

    return userInfo.name
  }

  @computed get fieldLookupKey() {
    const {field, workspace} = this
    return `${workspace?.id ?? 'No Workspace'} - ${field?.field}`
  }


  @computed get timestamp() {
    const {log: {timestamp}} = this

    return moment(timestamp).format('hh:mm A')
  }

  get isQuarterScreen() {
    return false
  }
}

export const ChangeLogSingleItemStoreContext = React.createContext<ChangeLogSingleItemStore>(null)
export const useChangeLogSingleItemStore = () => React.useContext(ChangeLogSingleItemStoreContext)

/// endregion

/// region Components

export type TSingleItemOperation = 'Added' | 'Changed' | 'Removed' | 'Inner Change' | 'Created' | 'Deleted'

type ISingleItemDiff = {
  before: any
  after: any
}

export interface ISingleChangeItem {
  id: string
  field: string
  op: TSingleItemOperation
  diff: ISingleItemDiff
}

interface MyProps {
  className?: string
  changed?: ISingleChangeItem
  nested?: string
  label?: string
  index?: number
}

const query = {
  'width-between-400-and-599': {
    // minWidth: 400,
    maxWidth: 599
  },
  'width-larger-than-600': {
    minWidth: 600
  }
}

/// endregion

export const ChangeLogSingleItem = observer<MyProps>(function ChangeLogSingleItem({className, ...props}) {
  const rootStore = useChangeLogFlexLayoutTabStore(),
    itemsStore = useChangeLogItemsStore(),
    obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new ChangeLogSingleItemStore(stores, itemsStore, rootStore, obsProps)),
    [params, containerRef] = useContainerQuery(query, null),
    isQuarterScreen = params['width-between-400-and-599']
  if (!store.workspace) {
    return null
  }
  const {id, operation, __html, imageCountLabel, userInfo, timestamp, checked, workspace: {catalog: {catalogItemName}}, itemsStore: {animateIn}} = store

  useEffect(() => store.registerEffects(), [])

  if (operation === undefined || operation === null) return null
  return (
    <ChangeLogSingleItemStoreContext.Provider value={store}>
      <div ref={containerRef} className={classNames(css.root, params, className)} data-animate-in={animateIn} data-width-quarter={params['width-between-400-and-599']} data-selected={checked}>
        <div className={css.left} data-left-width-quarter={params['width-between-400-and-599']}>
          <Checkbox data-checkbox='accordion' checked={checked} classes={{colorSecondary: css.colorSecondary}} onChange={(e) => {rootStore.onSelectChangeLog(e, id)}}/>
          <div className={css.leftContentWrapper}><span className={classNames(css.chip, imageCountLabel && css.chipWithImage)} data-variant={operation?.toLowerCase()}>{operation}</span>{__html}</div>
          {isQuarterScreen === true && <ChangeLogSingleItemMoreOptions/>}
        </div>
        <div className={css.right} data-right-width-quarter={params['width-between-400-and-599']}>
          <div className={css.time} data-variant={operation?.toLowerCase()}>{timestamp} by [{userInfo}] [<span data-type={'item'}>{catalogItemName}</span>]</div>
          {isQuarterScreen === false && <ChangeLogSingleItemMoreOptions/>}
        </div>
      </div>
    </ChangeLogSingleItemStoreContext.Provider>)
})



// WEBPACK FOOTER //
// ./src/modules/changelog/ChangeLogSingleItem.tsx